import React, { useEffect, useState, useRef } from 'react';
import { adminApi } from '../context/AdminContext';
import { IMAGE_BASE_URL } from '../constant';

const emptyForm = { product_id: '', name: '', slug: '', image: null };

export default function AdminSubProducts() {
  const [subProducts, setSubProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [alert, setAlert] = useState(null);
  const fileRef = useRef();

  const showAlert = (msg, type = 'success') => {
    setAlert({ msg, type });
    setTimeout(() => setAlert(null), 3500);
  };

  const fetchAll = () => {
    setFetching(true);
    Promise.all([
      adminApi.get('/admin/products'),
      adminApi.get('/admin/sub-products'),
    ])
      .then(([pRes, spRes]) => {
        setProducts(pRes.data);
        setSubProducts(spRes.data);
      })
      .catch(() => showAlert('Failed to load data', 'error'))
      .finally(() => setFetching(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'name' && !editId) {
        updated.slug = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      }
      return updated;
    });
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm(prev => ({ ...prev, image: file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.product_id) return showAlert('Please select a product', 'error');
    if (!form.name.trim()) return showAlert('Sub product name is required', 'error');
    setLoading(true);

    const data = new FormData();
    data.append('product_id', form.product_id);
    data.append('name', form.name);
    data.append('slug', form.slug);
    if (form.image) data.append('image', form.image);
    if (editId && existingImage) data.append('existing_image', existingImage);

    try {
      if (editId) {
        await adminApi.put(`/admin/sub-products/${editId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        showAlert('Sub product updated!');
      } else {
        await adminApi.post('/admin/sub-products', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        showAlert('Sub product added!');
      }
      setForm(emptyForm);
      setEditId(null);
      setPreview(null);
      setExistingImage(null);
      if (fileRef.current) fileRef.current.value = '';
      fetchAll();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Operation failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sp) => {
    setEditId(sp.id);
    setForm({ product_id: sp.product_id, name: sp.name, slug: sp.slug, image: null });
    setExistingImage(sp.image_url);
    setPreview(sp.image_url ? `${IMAGE_BASE_URL}${sp.image_url}` : null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await adminApi.delete(`/admin/sub-products/${id}`);
      showAlert('Sub product deleted.');
      fetchAll();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Delete failed', 'error');
    }
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditId(null);
    setPreview(null);
    setExistingImage(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const getProductName = (id) => {
    const p = products.find(p => p.id === id);
    return p ? (p.name || p.slug) : '—';
  };

  return (
    <div className="admin-subproducts-page">
      <h1 className="page-title">Manage Sub Products</h1>

      {alert && <div className={`alert alert-${alert.type}`}>{alert.msg}</div>}

      {/* Form */}
      <div className="admin-card">
        <div className="admin-card-header">
          <div className="admin-card-title">
            {editId ? '✏️ Edit Sub Product' : '➕ Add New Sub Product'}
          </div>
        </div>
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-row">
            <div className="form-group">
              <label>Parent Product *</label>
              <select name="product_id" value={form.product_id} onChange={handleChange} required>
                <option value="">— Select Product —</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.name || p.slug}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Sub Product Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Cherry Tomato F1"
                required
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Slug (URL)</label>
              <input
                name="slug"
                value={form.slug}
                onChange={handleChange}
                placeholder="e.g. cherry-tomato-f1"
              />
            </div>
            <div className="form-group">
              <label>Product Image</label>
              <input type="file" accept="image/*" onChange={handleFile} ref={fileRef} />
            </div>
          </div>

          {preview && (
            <div className="image-preview">
              <img src={preview} alt="Preview" />
              <button type="button" className="remove-img" onClick={() => { setPreview(null); setForm(p => ({ ...p, image: null })); setExistingImage(null); if (fileRef.current) fileRef.current.value = ''; }}>✕</button>
            </div>
          )}

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? '⏳ Saving...' : editId ? '✅ Update' : '➕ Add Sub Product'}
            </button>
            {editId && (
              <button type="button" className="btn-secondary" onClick={handleCancel}>Cancel</button>
            )}
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="admin-card">
        <div className="admin-card-header">
          <div className="admin-card-title">📋 All Sub Products ({subProducts.length})</div>
        </div>
        {fetching ? (
          <div className="loading-state">Loading...</div>
        ) : subProducts.length === 0 ? (
          <div className="empty-state">No sub products yet.</div>
        ) : (
          <div className="table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Product</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subProducts.map((sp, i) => (
                  <tr key={sp.id}>
                    <td>{i + 1}</td>
                    <td>
                      {sp.image_url ? (
                        <img
                          src={`${IMAGE_BASE_URL}${sp.image_url}`}
                          alt={sp.name}
                          className="thumb"
                        />
                      ) : (
                        <div className="no-img">🌿</div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{sp.name}</div>
                      <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{sp.slug}</div>
                    </td>
                    <td>
                      <span className="badge badge-green">{getProductName(sp.product_id)}</span>
                    </td>
                    <td>
                      <div className="actions">
                        <button className="btn-edit" onClick={() => handleEdit(sp)}>✏️ Edit</button>
                        <button className="btn-danger" onClick={() => handleDelete(sp.id, sp.name)}>🗑️ Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .admin-subproducts-page { max-width: 1000px; }
        .page-header { margin-bottom: 24px; }
        .page-title { font-size: 1.6rem; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
        .page-subtitle { color: #64748b; margin: 0; font-size: 0.95rem; }
        .image-preview { position: relative; display: inline-block; margin-bottom: 16px; }
        .image-preview img { width: 120px; height: 90px; object-fit: cover; border-radius: 10px; border: 2px solid #e2e8f0; }
        .remove-img { position: absolute; top: -8px; right: -8px; background: #ef4444; color: white; border: none; border-radius: 50%; width: 22px; height: 22px; cursor: pointer; font-size: 0.75rem; display: flex; align-items: center; justify-content: center; }
        .thumb { width: 52px; height: 40px; object-fit: cover; border-radius: 8px; }
        .no-img { width: 52px; height: 40px; background: #f1f5f9; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; }
        .table-wrapper { overflow-x: auto; }
        .loading-state, .empty-state { text-align: center; padding: 40px; color: #94a3b8; }
      `}</style>
    </div>
  );
}
